import supabase from '../lib/supabase';

// Helper to create a system notification in the database
const createSystemNotification = async (userId, type, title, message, relatedId) => {
  if (!userId) return;
  try {
    const { error } = await supabase
      .from('system_notifications_crm_2025')
      .insert([{
        user_id: userId,
        type,
        title,
        message,
        related_item_id: relatedId,
        is_read: false
      }]);
      
    if (error) console.error('Error creating system notification:', error);
  } catch (error) {
    console.error('System notification error:', error);
  }
};

// Internal helper to check if email should be sent based on settings
const shouldSendEmail = (settings, type) => {
  if (!settings || !settings.notificationPreferences) return true; // Default to true if no settings
  return settings.notificationPreferences[type] !== false;
};

// Helper to get user email if not provided
const getUserEmail = async (userId) => {
  if (!userId) return null;
  const { data, error } = await supabase
    .from('users_crm_2025')
    .select('email, full_name')
    .eq('id', userId)
    .single();
  if (error) return null;
  return data;
};

const logAndNotify = async (recipient, subject, body, type, relatedId, settings) => {
  // 1. Check Settings
  if (type !== 'test_email' && !shouldSendEmail(settings, type)) {
    console.log(`Creating email skipped for ${type} due to settings.`);
    return { success: true, skipped: true };
  }

  let status = 'sent';
  let errorDetails = null;
  let isSimulated = false;

  // 2. Log to console
  console.log(`
    --- 📧 EMAIL NOTIFICATION ---
    To: ${recipient}
    Subject: ${subject}
    Type: ${type}
    -----------------------------
  `);

  // 3. Attempt to send via Supabase Edge Function
  try {
    const { data, error: funcError } = await supabase.functions.invoke('send-email', {
      body: { 
        recipient, 
        subject, 
        body,
        type,
        apiKey: settings?.emailitApiKey,
        fromEmail: settings?.companyEmail // Pass the configured company email
      }
    });

    if (funcError) {
      throw funcError;
    }
  } catch (error) {
    // If the function is not deployed or fails, we fall back to a "local simulation"
    // so the user can still test the UI flow and database logging.
    console.warn('Email sending failed (Edge Function not reachable). Falling back to local simulation.', error.message);
    status = 'simulated_local';
    isSimulated = true;
    errorDetails = error.message;
  }

  // 4. Insert into Supabase table (Email Log)
  try {
    const { error } = await supabase
      .from('email_notifications_crm_2025')
      .insert([{
        recipient,
        subject,
        body,
        notification_type: type,
        related_item_id: relatedId,
        status: status
      }]);

    if (error && error.code !== '42P01') {
      console.warn('Email log insert warning:', error.message);
    }
  } catch (error) {
    console.error('Error logging notification:', error);
  }

  // Return success if it was sent OR if we successfully simulated it locally
  // We include the 'simulated' flag so the UI can inform the user.
  return { 
    success: true, 
    simulated: isSimulated,
    error: isSimulated ? 'Edge Function not deployed' : null 
  };
};

const sendTestEmail = async (recipient, settings) => {
  const subject = 'Test Email from CRM System';
  const body = `
    Hello,

    This is a test email sent from your CRM system to verify email functionality.
    
    Time: ${new Date().toLocaleString()}
    
    If you received this, your email configuration works!

    Best regards,
    CRM System
  `;

  return await logAndNotify(recipient, subject, body, 'test_email', null, settings);
};

const sendNewUserWelcomeEmail = async (user, defaultPassword, settings) => {
  const subject = 'Welcome to the CRM!';
  const body = `
    Hello ${user.full_name},

    Welcome to our CRM system! Your account has been created.
    You can log in using the following credentials:

    Email: ${user.email}
    Password: ${defaultPassword}

    Please change your password after your first login.

    Best regards,
    The CRM Team
  `;

  // Always create system notification
  await createSystemNotification(
    user.id, 
    'welcome', 
    'Welcome to CRM', 
    'Your account has been set up successfully.', 
    user.id
  );

  return await logAndNotify(user.email, subject, body, 'new_user_welcome', user.id, settings);
};

const sendPasswordChangedEmail = async (user, settings) => {
  const subject = 'Your CRM Password Has Been Changed';
  const body = `
    Hello ${user.full_name},

    This is a confirmation that the password for your account ${user.email} has just been changed.
    If you did not make this change, please contact your administrator immediately.

    Best regards,
    The CRM Team
  `;

  await createSystemNotification(
    user.id, 
    'security', 
    'Password Changed', 
    'Your password was recently updated.', 
    user.id
  );

  return await logAndNotify(user.email, subject, body, 'password_changed', user.id, settings);
};

const sendTaskAssignedEmail = async (task, assignedUser, settings) => {
  if (!assignedUser || !assignedUser.email) return;

  const subject = `New Task Assigned: ${task.title}`;
  const body = `
    Hello ${assignedUser.full_name || assignedUser.fullName},

    A new task has been assigned to you:
    
    Task: ${task.title}
    Priority: ${task.priority}
    Due Date: ${task.due_date || task.dueDate ? new Date(task.due_date || task.dueDate).toLocaleDateString() : 'Not set'}

    Please log in to the CRM to view the details.

    Best regards,
    The CRM Team
  `;

  await createSystemNotification(
    assignedUser.id,
    'task_assigned',
    'New Task Assigned',
    `You have been assigned a new task: ${task.title}`,
    task.id
  );

  return await logAndNotify(assignedUser.email, subject, body, 'task_assigned', task.id, settings);
};

const sendTaskStatusUpdateEmail = async (task, newStatus, creator, assignee, settings) => {
  if (!creator || !creator.email) return;
  if (creator.id === assignee.id) return;

  const subject = `Task Status Updated: ${task.title}`;
  const body = `
    Hello ${creator.full_name || creator.fullName},

    The status of a task you created has been updated by ${assignee.full_name || assignee.fullName}.
    
    Task: ${task.title}
    New Status: ${newStatus.replace('-', ' ')}

    Best regards,
    The CRM Team
  `;

  await createSystemNotification(
    creator.id,
    'task_update',
    'Task Updated',
    `${assignee.full_name || assignee.fullName} updated status of "${task.title}" to ${newStatus.replace('-', ' ')}`,
    task.id
  );

  return await logAndNotify(creator.email, subject, body, 'task_status_updated', task.id, settings);
};

const sendLeadAssignedEmail = async (lead, assignedUser, settings) => {
  if (!assignedUser || !assignedUser.email) return;

  const subject = `New Lead Assigned: ${lead.lead_name}`;
  const body = `
    Hello ${assignedUser.full_name || assignedUser.fullName},

    A new lead has been assigned to you:
    
    Lead Name: ${lead.lead_name}
    Company: ${lead.company || 'N/A'}
    Priority: ${lead.priority}

    Please log in to the CRM to view the details and follow up.

    Best regards,
    The CRM Team
  `;

  await createSystemNotification(
    assignedUser.id,
    'lead_assigned',
    'New Lead Assigned',
    `You have been assigned a new lead: ${lead.lead_name}`,
    lead.id
  );

  return await logAndNotify(assignedUser.email, subject, body, 'lead_assigned', lead.id, settings);
};

const sendContractRenewalReminder = async (contract, client, settings) => {
  if (!client || !client.email) return;

  const subject = `Contract Renewal Reminder - ${contract.title}`;
  const body = `
    Dear ${client.name},

    This is a friendly reminder that your contract "${contract.title}" is due for renewal on ${new Date(contract.endDate).toLocaleDateString()}.
    
    Please contact us to discuss your renewal options.

    Best regards,
    The CRM Team
  `;

  return await logAndNotify(client.email, subject, body, 'contract_renewal', contract.id, settings);
};

const sendChangeRequestCreatedEmail = async (request, contract, client, admins, settings) => {
  if (!admins || admins.length === 0) return;

  const subject = `New Change Request: ${request.title}`;
  const body = `
    Hello Admin,

    A new change request has been submitted.

    Title: ${request.title}
    Contract: ${contract.title}
    Client: ${client?.name || 'N/A'}
    Priority: ${request.priority}

    Please review it in the CRM.

    Best regards,
    The CRM Team
  `;

  // Notify all admins
  for (const admin of admins) {
    if (admin.email) {
      await createSystemNotification(
        admin.id,
        'change_request',
        'New Change Request',
        `New request "${request.title}" created.`,
        request.id
      );
      
      await logAndNotify(admin.email, subject, body, 'change_request_created', request.id, settings);
    }
  }
};

const sendChangeRequestStatusUpdateEmail = async (request, contract, settings) => {
  // If we don't have creator info, try to fetch it
  let recipientEmail = null;
  let recipientId = null;
  let recipientName = 'User';

  if (request.createdBy) {
    recipientId = request.createdBy;
    const userData = await getUserEmail(request.createdBy);
    if (userData) {
      recipientEmail = userData.email;
      recipientName = userData.full_name;
    }
  }

  if (!recipientEmail) return;

  const subject = `Change Request Updated: ${request.title}`;
  const body = `
    Hello ${recipientName},

    The status of your change request has been updated.

    Title: ${request.title}
    Contract: ${contract?.title || 'N/A'}
    New Status: ${request.status.toUpperCase()}

    Best regards,
    The CRM Team
  `;

  await createSystemNotification(
    recipientId,
    'change_request_update',
    'Change Request Updated',
    `Request "${request.title}" is now ${request.status}.`,
    request.id
  );

  return await logAndNotify(recipientEmail, subject, body, 'change_request_updated', request.id, settings);
};

export const NotificationService = {
  sendNewUserWelcomeEmail,
  sendPasswordChangedEmail,
  sendTaskAssignedEmail,
  sendTaskStatusUpdateEmail,
  sendLeadAssignedEmail,
  sendContractRenewalReminder,
  sendChangeRequestCreatedEmail,
  sendChangeRequestStatusUpdateEmail,
  sendTestEmail
};